import { createMessage } from './codec';
import { Transport } from './transport';
import { MessageType, ConnectionState, type Message, type HelloPayload } from './types';

export interface HandshakeConfig {
  role: 'host' | 'game';
  helloInterval?: number;
  debug?: boolean;
  /** Кастомный логгер. По умолчанию `console.log` при `debug=true`. */
  logger?: (...args: unknown[]) => void;
}

export interface HandshakeCallbacks {
  onStateChange?: (state: ConnectionState) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export class Handshake {
  private config: HandshakeConfig;
  private transport: Transport;
  private callbacks: HandshakeCallbacks;
  private state: ConnectionState = ConnectionState.INIT;
  private helloIntervalId?: ReturnType<typeof setInterval>;

  public constructor(transport: Transport, config: HandshakeConfig, callbacks: HandshakeCallbacks = {}) {
    this.transport = transport;
    this.config = { helloInterval: 1000, ...config };
    this.callbacks = callbacks;
  }

  public start(): void {
    this.log('Starting handshake');
    this.setState(ConnectionState.CONNECTING);
    this.startHelloLoop();
  }

  public handleMessage(message: Message): void {
    switch (message.type) {
      case MessageType.HELLO:
        this.handleHello(message);
        break;
      case MessageType.HELLO_ACK:
        this.handleHelloAck();
        break;
    }
  }

  public getState(): ConnectionState {
    return this.state;
  }

  public isConnected(): boolean {
    return this.state === ConnectionState.CONNECTED;
  }

  public disconnect(): void {
    this.stopHelloLoop();
    this.setState(ConnectionState.DISCONNECTED);
    this.callbacks.onDisconnected?.();
  }

  private startHelloLoop(): void {
    this.sendHello();
    this.helloIntervalId = setInterval(() => {
      if (this.state !== ConnectionState.CONNECTED) {
        this.sendHello();
      }
    }, this.config.helloInterval);
  }

  private stopHelloLoop(): void {
    if (this.helloIntervalId) {
      clearInterval(this.helloIntervalId);
      this.helloIntervalId = undefined;
    }
  }

  private sendHello(): void {
    const payload: HelloPayload = { role: this.config.role };
    const message = createMessage(MessageType.HELLO, payload);
    this.transport.send(message);
    this.log('Sent HELLO');
  }

  private handleHello(message: Message): void {
    this.log('Received HELLO');
    const ackMessage = createMessage(MessageType.HELLO_ACK, {}, message.id);
    this.transport.send(ackMessage);
    this.log('Sent HELLO_ACK');

    if (this.state !== ConnectionState.CONNECTED) {
      this.completeHandshake();
    }
  }

  private handleHelloAck(): void {
    this.log('Received HELLO_ACK');
    if (this.state !== ConnectionState.CONNECTED) {
      this.completeHandshake();
    }
  }

  private completeHandshake(): void {
    if (this.state === ConnectionState.CONNECTED) return;

    this.stopHelloLoop();
    this.setState(ConnectionState.CONNECTED);

    this.callbacks.onConnected?.();
  }

  private setState(newState: ConnectionState): void {
    if (this.state !== newState) {
      this.log(`State: ${this.state} -> ${newState}`);
      this.state = newState;
      this.callbacks.onStateChange?.(newState);
    }
  }

  private log(...args: unknown[]): void {
    if (!this.config.debug) return;
    const tagged = [`[Handshake:${this.config.role}]`, ...args];
    (this.config.logger ?? console.log)(...tagged);
  }
}
